const SETTLEMENT_KEY = /^[A-Za-z0-9_-]{1,128}:[A-Za-z0-9_-]{1,128}$/;

export interface RecorderSettlementLock {
  runExclusive<T>(settlementKey: string, operation: () => Promise<T>): Promise<T>;
  isTerminalIntentActive(settlementKey: string): Promise<boolean>;
  holdTerminalIntent(settlementKey: string): Promise<() => void>;
}

/**
 * Serializes one settlement across same-origin tabs. Web Locks are released by
 * the browser if a tab exits, so a crashed sender cannot leave a permanent lock.
 */
export class BrowserRecorderSettlementLock implements RecorderSettlementLock {
  constructor(
    private readonly lockManager: Pick<LockManager, 'request'> | undefined
      = globalThis.navigator?.locks,
  ) {}

  async runExclusive<T>(settlementKey: string, operation: () => Promise<T>): Promise<T> {
    this.validateKey(settlementKey);
    if (!this.lockManager) return operation();
    return this.lockManager.request(
      `vd-recorder-settlement:${settlementKey}`,
      { mode: 'exclusive' },
      operation,
    ).then((result) => result);
  }

  async isTerminalIntentActive(settlementKey: string): Promise<boolean> {
    this.validateKey(settlementKey);
    if (!this.lockManager) return false;
    return this.lockManager.request(
      `vd-recorder-terminal-intent:${settlementKey}`,
      { mode: 'exclusive', ifAvailable: true },
      (lock) => lock === null,
    );
  }

  async holdTerminalIntent(settlementKey: string): Promise<() => void> {
    this.validateKey(settlementKey);
    if (!this.lockManager) return () => {};
    let release!: () => void;
    const released = new Promise<void>((resolve) => { release = resolve; });
    let markAcquired!: () => void;
    let markFailed!: (error: unknown) => void;
    const acquired = new Promise<void>((resolve, reject) => {
      markAcquired = resolve;
      markFailed = reject;
    });
    const held = this.lockManager.request(
      `vd-recorder-terminal-intent:${settlementKey}`,
      { mode: 'exclusive' },
      async () => {
        markAcquired();
        await released;
      },
    ).then((result) => result);
    void held.catch(markFailed);
    await acquired;
    return () => {
      release();
      void held.catch(() => {});
    };
  }

  private validateKey(settlementKey: string): void {
    if (!SETTLEMENT_KEY.test(settlementKey)) {
      throw new Error('Invalid recording settlement lock key.');
    }
  }
}
